import { useEffect, useState } from 'react';
import { Popover } from 'antd';

import { Input } from 'antd';
import axios from 'axios';
import moment from 'moment';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import { SPOTIFY_TOKEN } from '../../constants/storage';
import { useLoginContext } from '../../context';
import { usePlaylistContext } from '../../context/PlaylistContext';
import {
  addItemToStorage,
  addSongToPlaylist,
  getPlaylistItemsSpotify,
  getRefreshedToken,
  isSpotifyTrackURI,
} from '../../utils';
import {
  useAccountCampaignAccept,
  type CampaignSongPlacement,
} from '../../hooks/useAccountCampaignAccept';
import type { LinkedCuratorProfile } from '../../utils/accountCuratorOffer';
import { getSiblingProfileObjectIdForAdd } from '../../utils/authAccount';
import {
  buildInstantShareDetail,
  formatPlaylistAddError,
  isPastStartTime,
  PAST_START_TIME_MESSAGE,
  persistInstantShareDetail,
  resolvePlacementStartTime,
  toSafeIsoString,
  validateAddToPlaylistForm,
  ADD_TO_PLAYLIST_DATE_QUICK_OFFSETS,
} from '../../utils/addToPlaylistForm';
import DatePicker from '../DatePicker/DatePickerWithoutFormik';
import PlanToast from '../PlanToast/PlanToast';
import Spinner from '../Spinner/Spinner';
import { AddToPlayListModalStyle } from './AddToPlayListModal.style';

const EMPTY_LINKED_PROFILES: LinkedCuratorProfile[] = [];

// Helper function to validate Spotify URL
const isSpotifyURL = (url: string): boolean => {
  if (!url) return false;
  try {
    const urlPattern = /^https:\/\/open\.spotify\.com\/track\/[a-zA-Z0-9]+(\?.*)?$/;
    return urlPattern.test(url);
  } catch {
    return false;
  }
};

// Helper function to convert Spotify URL to URI
const convertURLtoURI = (url: string): string => {
  try {
    const match = url.match(/https:\/\/open\.spotify\.com\/track\/([a-zA-Z0-9]+)/);
    if (match && match[1]) {
      return `spotify:track:${match[1]}`;
    }
    return url;
  } catch {
    return url;
  }
};

interface AddToPlayListModalProps {
  isModalVisible?: boolean;
  hideUriInput?: boolean;
  preRelease?: boolean;
  hideRadioButtons?: boolean;
  setIsModalVisible?: (_: boolean) => void;
  songURI?: string;
  setSongURI?: (_: string) => void;
  existSongUri?: string;
  getFuturelist?: any;
  editObj?: any;
  hideArtistAndSongName?: boolean;
  isFromCampaign?: boolean;
  campaignData?: any; // Campaign data when song is added from campaign page
  linkedProfiles?: LinkedCuratorProfile[];
  accountUserId?: string;
  /** Pre-select playlist when opening from a playlist page (id + display name). */
  initialPlaylist?: { id: string; name: string };
}

// Define a type for the campaign info object
type CampaignSongInfo = CampaignSongPlacement;

const AddToPlayListModal = (props: AddToPlayListModalProps) => {
  const {
    isModalVisible,
    setIsModalVisible,
    hideRadioButtons,
    hideUriInput,
    preRelease,
    existSongUri,
    editObj,
    hideArtistAndSongName,
    songURI,
    isFromCampaign,
    campaignData,
    linkedProfiles = EMPTY_LINKED_PROFILES,
    accountUserId,
    initialPlaylist,
  } = props;
  const { playlistId } = useParams();
  const [setLoading, showLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem('userInfo'));
  const [limitText, setLimitText] = useState('');
  const [enableDatePicker, setEnableDatePicker] = useState(true);

  const { state } = usePlaylistContext();
  const { state: loginState, dispatch: loginDispatch } = useLoginContext();

  const isUnreleasedCampaign = !!(isFromCampaign && campaignData && !campaignData.track_released);
  const allowCampaignFlowForFree = isFromCampaign === true;
  const canUseCampaignSchedulingControls = !user?.free || allowCampaignFlowForFree;

  // Debug logging
  // console.log('[AddToPlayListModal] isUnreleasedCampaign calculation:', {
  //   isFromCampaign,
  //   campaignDataExists: !!campaignData,
  //   track_released: campaignData?.track_released,
  //   isUnreleasedCampaign
  // });

  const [setButtom, showButtom] = useState('');
  const [setDate, showDate] = useState(new Date());
  const [release, setRelease] = useState('Immediately');
  const [selectPlayListOpen, setSelectPlayListOpen] = useState(false);
  const [selectedPlayList, setSelectedPlayList] = useState([]);
  const [totalPlaylist, setTotalPlaylist] = useState([]);
  const [totalPlaylistNumber, setTotalPlaylistNumber] = useState(0);
  const [note, setNote] = useState('');
  const [searchPlaylistText, setSearchPlaylistText] = useState('');
  const [arrayPositions, setArrayPositions] = useState([]);
  const [positionText, setPositionText] = useState(0);

  const [data, setData] = useState({
    playlistId: [],
    startTime: '',
    endTime: '',
    position: 1,
    status: 'past',
    searchedSongData: '',
    artist_name: '',
    song_name: '',
  });
  const [inputType, setInputType] = useState<'uri' | 'url'>('uri');

  // Phase 2: Song preview states
  const [songPreview, setSongPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const {
    addCampaignSongToSelectedPlaylists,
    getPlaylistSession,
    isAccountPlaylistPickerLoading,
    mapCampaignPlaylistItems,
    notifyBackendAboutAddedSong,
    resetCampaignAcceptState,
    usesAccountCampaignPlaylists,
    usesAccountWidePlaylists,
    usesMultiProfilePlaylistMode,
    activeProfileId,
  } = useAccountCampaignAccept({
    isModalVisible,
    isFromCampaign,
    campaignData,
    linkedProfiles,
    accountUserId,
    loginState,
    release,
    setDate,
    note,
    userName: user?.name,
    totalPlaylist,
    setLoading: showLoading,
  });

  const resolvePlaylistAuth = (targetPlaylistId: string) => {
    const session = getPlaylistSession(targetPlaylistId);
    const profileObjectId = getSiblingProfileObjectIdForAdd(
      session?.profileId,
      activeProfileId
    );
    return {
      accessToken: session?.accessToken || loginState.token,
      refreshToken: profileObjectId ? '_' : loginState.refreshToken,
      spotifyUserId: session?.spotifyUserId || loginState.userId,
      profileObjectId,
    };
  };

  const handleOk = () => {
    const preservedURI = props.songURI || '';

    setIsModalVisible(false);
    setData({
      playlistId: [],
      startTime: '',
      endTime: '',
      position: 1,
      status: 'past',
      searchedSongData: preservedURI,
      artist_name: '',
      song_name: '',
    });
    setNote('');
    setSelectedPlayList([]);
    setTotalPlaylist([]);
    setTotalPlaylistNumber(0);
    showDate(new Date());
    setSearchPlaylistText('');
    setSelectPlayListOpen(false);
    setSongPreview(null);
    setPreviewLoading(false);
    resetCampaignAcceptState();
  };

  const completeAccountCampaignAccept = (firstPlacement: CampaignSongPlacement) => {
    handleOk();
    persistInstantShareDetail(
      buildInstantShareDetail({
        songUri: firstPlacement.songURI,
        playlistId: firstPlacement.playlistId,
        position: firstPlacement.songPosition,
        startTime: firstPlacement.startTime,
        scheduleDate: setDate,
        endTime: firstPlacement.endTime || data.endTime,
      })
    );
    setTimeout(() => {
      window.location.href = '/campaigns?songAdded=true';
    }, 1000);
  };

  const completeRegularPlaylistAdd = (firstPlacement: CampaignSongPlacement) => {
    persistInstantShareDetail(
      buildInstantShareDetail({
        songUri: firstPlacement.songURI,
        playlistId: firstPlacement.playlistId,
        position: firstPlacement.songPosition,
        startTime: firstPlacement.startTime,
        scheduleDate: setDate,
        endTime: firstPlacement.endTime || data.endTime,
      })
    );
    handleOk();
    window.location.reload();
  };
  const handleSwitch = value => {
    setEnableDatePicker(!enableDatePicker);
    // //console.log(value);
    if (value === true) {
      showButtom('disabled');
    } else {
      showButtom('');
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setData({
      ...data,
      searchedSongData: existSongUri,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existSongUri]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (songURI) {
      setData(prevData => ({
        ...prevData,
        searchedSongData: songURI,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [songURI]);

  useEffect(() => {
    if (isModalVisible && songURI) {
      setData(prevData => ({
        ...prevData,
        searchedSongData: songURI,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalVisible, songURI]);

  // Phase 2: Fetch song preview when URL is entered
  useEffect(() => {
    // Only fetch if URL mode is selected and input is not empty
    if (inputType !== 'url' || !data.searchedSongData) {
      setSongPreview(null);
      // Clear artist and song name fields when switching away from URL mode or clearing input
      // But don't clear if this is from a campaign (they should be pre-filled)
      if (!isFromCampaign) {
        setData(prevData => ({
          ...prevData,
          artist_name: '',
          song_name: '',
        }));
      }
      return;
    }

    // Validate URL
    if (!isSpotifyURL(data.searchedSongData)) {
      setSongPreview(null);
      // Clear artist and song name fields when URL is invalid
      if (!isFromCampaign) {
        setData(prevData => ({
          ...prevData,
          artist_name: '',
          song_name: '',
        }));
      }
      return;
    }

    // Debounce API call
    const timeoutId = setTimeout(() => {
      const trackId = data.searchedSongData.match(/track\/([a-zA-Z0-9]+)/)?.[1];
      if (!trackId) return;

      setPreviewLoading(true);
      axios
        .get(`https://api.spotify.com/v1/tracks/${trackId}`, {
          headers: {
            Authorization: `Bearer ${loginState.token}`,
          },
        })
        .then(response => {
          setSongPreview(response.data);
          setPreviewLoading(false);
          // Populate artist and song name fields from fetched data
          if (response.data) {
            setData(prevData => ({
              ...prevData,
              artist_name: response.data.artists?.map(a => a.name).join(', ') || '',
              song_name: response.data.name || '',
            }));
          }
        })
        .catch(async error => {
          setPreviewLoading(false);
          setSongPreview(null);
          // Clear artist and song name fields on API error
          setData(prevData => ({
            ...prevData,
            artist_name: '',
            song_name: '',
          }));
          // Handle token refresh if needed
          if (error.response?.status === 401) {
            try {
              const refreshResponse = await getRefreshedToken(loginState.refreshToken);
              addItemToStorage(SPOTIFY_TOKEN, refreshResponse?.data?.access_token);
              loginDispatch({
                type: 'ADD_TOKEN',
                payload: refreshResponse?.data?.access_token,
              });
            } catch (refreshError) {
              // Silent fail - user can still submit
            }
          }
        });
    }, 800); // 800ms debounce

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputType, data.searchedSongData, loginState.token]);

  const handleCancel = (e: any) => {
    e.stopPropagation();
    // Preserve songURI from props if it exists, otherwise clear it
    const preservedURI = props.songURI || '';

    setData({
      playlistId: [],
      startTime: '',
      endTime: '',
      position: 1,
      status: 'future',
      searchedSongData: preservedURI,
      artist_name: '',
      song_name: '',
    });
    setNote('');
    setSelectedPlayList([]);
    setIsModalVisible(false);
    setTotalPlaylist([]);
    setTotalPlaylistNumber(0);
    showDate(new Date());
    setSearchPlaylistText('');
    setSelectPlayListOpen(false);
    // Phase 2: Clear preview
    setSongPreview(null);
    setPreviewLoading(false);
  };

  const getPlaylists = offset => {
    axios(`https://api.spotify.com/v1/me/playlists?limit=50&offset=${offset}`, {
      method: 'GET',
      headers: { Authorization: 'Bearer ' + loginState.token },
    })
      .then(genreResponse => {
        setTotalPlaylist([...totalPlaylist, ...genreResponse?.data.items]);
        if (totalPlaylistNumber < genreResponse?.data.total) {
          setTotalPlaylistNumber(genreResponse?.data.total);
        }
      })
      .catch(async error => {
        if (error.response?.data?.error?.status === 401 || error.response?.status === 400) {
          getRefreshedToken(state.refreshToken)
            .then(response => {
              addItemToStorage(SPOTIFY_TOKEN, response?.data?.access_token);

              loginDispatch({
                type: 'ADD_TOKEN',
                payload: response?.data?.access_token,
              });
            })
            .catch(() => {});
        }
      });
  };
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (usesMultiProfilePlaylistMode || isAccountPlaylistPickerLoading) {
      return;
    }
    if (
      isModalVisible &&
      (totalPlaylist.length < totalPlaylistNumber || totalPlaylist.length === 0)
    ) {
      getPlaylists(totalPlaylist.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isModalVisible,
    totalPlaylistNumber,
    totalPlaylist,
    usesMultiProfilePlaylistMode,
    isAccountPlaylistPickerLoading,
  ]);

  const onHandle = (date, dateString) => {
    if (date) {
      showDate(date instanceof Date ? date : new Date(date));
    }
  };

  const addToPlaylistLoop = async data => {
    if (isAccountPlaylistPickerLoading) {
      showLoading(false);
      return;
    }

    let songData = data.searchedSongData;
    if (
      isFromCampaign &&
      campaignData &&
      (!songData || songData === '') &&
      campaignData.spotify_uri
    ) {
      data = {
        ...data,
        searchedSongData: campaignData.spotify_uri,
        artist_name: data.artist_name || campaignData.artist_name,
        song_name: data.song_name || campaignData.track_name,
      };
      songData = data.searchedSongData;
    }

    if (inputType === 'url') {
      if (!isSpotifyURL(songData)) {
        toast.error('Please enter a valid Spotify URL');
        showLoading(false);
        return;
      }
      songData = convertURLtoURI(songData);
    }

    if (!isSpotifyTrackURI(songData)) {
      toast.error('URI is incorrect');
      showLoading(false);
      return;
    }

    data = { ...data, searchedSongData: songData };
    const playlistIds = Array.isArray(data.playlistId) ? data.playlistId : [data.playlistId];
    const usesNumberPositionInput =
      isFromCampaign || selectedPlayList.length > 1 || playlistIds.length > 1;

    const validation = validateAddToPlaylistForm({
      playlistIds,
      position: data.position,
      arrayPositions,
      usesNumberPositionInput,
      searchedSongData: songData,
      isFromCampaign,
      endTime: data.endTime,
    });

    if (validation.ok === false) {
      toast.error(validation.message);
      showLoading(false);
      return;
    }

    const formData = { ...data, position: validation.position };

    if (usesAccountCampaignPlaylists || usesAccountWidePlaylists) {
      const firstPlacement = await addCampaignSongToSelectedPlaylists({
        ...formData,
        playlistId: playlistIds.length === 1 ? playlistIds[0] : playlistIds,
      });
      if (firstPlacement) {
        if (usesAccountCampaignPlaylists) {
          completeAccountCampaignAccept(firstPlacement);
        } else {
          completeRegularPlaylistAdd(firstPlacement);
        }
      }
      return;
    }

    if (playlistIds.length > 1) {
      playlistIds.forEach((playlistItemId, index) => {
        getPlaylistItemsSpotify(playlistItemId, resolvePlaylistAuth(playlistItemId).accessToken)
          .then(async res => {
            if (res.total === 0) {
              await handleFormSubmit(
                { ...formData, position: 0, playlistId: playlistItemId },
                playlistIds.length - 1 === index
              );
            } else if (formData.position > res.total) {
              await handleFormSubmit(
                { ...formData, position: res.total + 1, playlistId: playlistItemId },
                playlistIds.length - 1 === index
              );
            } else {
              await handleFormSubmit(
                { ...formData, playlistId: playlistItemId },
                playlistIds.length - 1 === index
              );
            }
          })
          .catch(error => {
            console.error('Failed to load playlist items:', error);
            toast.error(
              formatPlaylistAddError(error, 'Could not load playlist details from Spotify.')
            );
            if (playlistIds.length - 1 === index) {
              showLoading(false);
            }
          });
      });
      return;
    }

    if (isFromCampaign) {
      getPlaylistItemsSpotify(playlistIds[0], resolvePlaylistAuth(playlistIds[0]).accessToken)
        .then(async res => {
          if (res.total === 0) {
            await handleFormSubmit({ ...formData, position: 0, playlistId: playlistIds[0] }, true);
          } else if (formData.position > res.total) {
            await handleFormSubmit(
              { ...formData, position: res.total + 1, playlistId: playlistIds[0] },
              true
            );
          } else {
            await handleFormSubmit({ ...formData, playlistId: playlistIds[0] }, true);
          }
        })
        .catch(error => {
          console.error('Failed to load playlist items:', error);
          toast.error(formatPlaylistAddError(error, 'Could not load playlist details from Spotify.'));
          showLoading(false);
        });
      return;
    }

    validation.positions.forEach((positionValue, index) => {
      getPlaylistItemsSpotify(playlistIds[0], resolvePlaylistAuth(playlistIds[0]).accessToken)
        .then(async res => {
          if (res.total === 0) {
            await handleFormSubmit(
              { ...formData, position: index + 1, playlistId: playlistIds[0] },
              validation.positions.length - 1 === index
            );
          } else if (Number(positionValue) > res.total) {
            await handleFormSubmit(
              { ...formData, position: res.total + 1, playlistId: playlistIds[0] },
              validation.positions.length - 1 === index
            );
          } else {
            await handleFormSubmit(
              { ...formData, playlistId: playlistIds[0], position: positionValue },
              validation.positions.length - 1 === index
            );
          }
        })
        .catch(error => {
          console.error('Failed to load playlist items:', error);
          toast.error(formatPlaylistAddError(error, 'Could not load playlist details from Spotify.'));
          if (validation.positions.length - 1 === index) {
            showLoading(false);
          }
        });
    });
  };

  const getPlaylistItems = playlists => mapCampaignPlaylistItems(playlists);

  useEffect(() => {
    if (!isModalVisible) {
      return;
    }

    if (editObj?.selected_playlist_id) {
      const playlistItems = getPlaylistItems(totalPlaylist);
      if (!playlistItems.length && !usesMultiProfilePlaylistMode && !initialPlaylist?.name) {
        return;
      }

      const matched = playlistItems.find(obj => editObj.selected_playlist_id === obj.key);
      const displayName = matched?.value || initialPlaylist?.name;

      if (displayName) {
        setSelectedPlayList([displayName]);
      }
      setData({
        playlistId:
          typeof editObj.selected_playlist_id === 'string'
            ? [editObj.selected_playlist_id]
            : editObj.selected_playlist_id,
        startTime: editObj.start_time === 'null' ? null : editObj.start_time,
        endTime: editObj.end_time === 'null' ? null : editObj.end_time,
        position: Number(editObj.position) + 1,
        status: editObj.status,
        searchedSongData: editObj.song_uri,
        artist_name: editObj.artist_name,
        song_name: editObj.song_name,
      });
      setNote(editObj.notes);
      return;
    }

    const targetPlaylistId = initialPlaylist?.id;
    if (!targetPlaylistId) {
      setSelectedPlayList([]);
      setData(prev => ({
        ...prev,
        playlistId: [],
      }));
      return;
    }

    const playlistItems = getPlaylistItems(totalPlaylist);
    const matched = playlistItems.find(obj => obj.key === targetPlaylistId);
    const displayName = matched?.value || initialPlaylist?.name;

    setData(prev => ({
      ...prev,
      playlistId: [targetPlaylistId],
    }));

    if (displayName) {
      setSelectedPlayList([displayName]);
    }
  }, [
    editObj,
    initialPlaylist?.id,
    initialPlaylist?.name,
    isModalVisible,
    mapCampaignPlaylistItems,
    totalPlaylist,
    usesMultiProfilePlaylistMode,
  ]);

  const pre_Release = window.location.pathname === '/future_playlist';

  // Function to get campaign ID from props
  const getCampaignId = () => {
    if (isFromCampaign && campaignData && campaignData._id) {
      return campaignData._id;
    }
    return null;
  };

  const handleFormSubmit = async (values: any, lastValue: boolean) => {
    if (isFromCampaign && campaignData && !values.searchedSongData && campaignData.spotify_uri) {
      values.searchedSongData = campaignData.spotify_uri;
    }

    const targetPlaylistId = Array.isArray(values.playlistId)
      ? values.playlistId[0]
      : values.playlistId;
    const playlistAuth = resolvePlaylistAuth(targetPlaylistId);
    const positionCheck = validateAddToPlaylistForm({
      playlistIds: [targetPlaylistId],
      position: values.position,
      usesNumberPositionInput: true,
      searchedSongData: values.searchedSongData,
      isFromCampaign,
      endTime: values.endTime,
    });

    if (positionCheck.ok === false) {
      toast.error(positionCheck.message);
      showLoading(false);
      return;
    }

    values = { ...values, position: positionCheck.position };
    if (values.playlistId !== '' || values.endTime !== '') {
      if (window.location.pathname === '/future_playlist') {
        const scheduledStart = values.startTime ? new Date(values.startTime) : new Date();
        if (isPastStartTime(scheduledStart)) {
          toast.error(PAST_START_TIME_MESSAGE);
          showLoading(false);
          return;
        }
        if (values.searchedSongData.includes('spotify:track:')) {
          if (scheduledStart) {
            // Check if end date is required for campaigns
            if (isFromCampaign && !values.endTime) {
              toast.error('End date is required for campaigns');
              showLoading(false);
              return;
            }

            addSongToPlaylist(
              Array.isArray(values.playlistId) ? values.playlistId : [values.playlistId],
              values.searchedSongData,
              playlistAuth.accessToken,
              playlistAuth.refreshToken,
              playlistAuth.spotifyUserId,
              values.position,
              scheduledStart,
              values.endTime,
              'future',
              note,
              getCampaignId(),
              values.artist_name,
              values.song_name,
              playlistAuth.profileObjectId
            )
              .then(async response => {
                if (lastValue) {
                  // Get playlist details for notification
                  const playlistDetails = totalPlaylist.find(
                    playlist => playlist.id === values.playlistId
                  );
                  const playlistName = playlistDetails?.name || 'Unknown Playlist';

                  // Create campaign song info
                  // console.log('[AddToPlayListModal] Before creating songInfo:', {
                  //   isFromCampaign,
                  //   campaignData,
                  //   track_released: campaignData?.track_released
                  // });
                  const isCurrentlyUnreleased = !!(
                    isFromCampaign &&
                    campaignData &&
                    !campaignData.track_released
                  );
                  // console.log('[AddToPlayListModal] isCurrentlyUnreleased:', isCurrentlyUnreleased);
                  const songInfo: CampaignSongInfo = {
                    campaignId: getCampaignId(),
                    artistName: values.artist_name,
                    trackName: values.song_name,
                    songPosition: values.position,
                    playlistId: values.playlistId,
                    playlistName: playlistName,
                    songURI: values.searchedSongData,
                    songShareLink: `https://open.spotify.com/track/${values.searchedSongData.split(':')[2]}`,
                    addedByCuratorId: loginState.userId,
                    curatorName: user?.name || '',
                    status: 'future',
                    startTime: toSafeIsoString(scheduledStart),
                    endTime: values.endTime,
                    notes: note,
                    addedBy: '', // This will be set in notifyBackendAboutAddedSong
                    isScheduled: isCurrentlyUnreleased,
                    scheduledStartTime: isCurrentlyUnreleased
                      ? toSafeIsoString(scheduledStart)
                      : null,
                  };
                  // console.log('[AddToPlayListModal] songInfo created:', {
                  //   isScheduled: songInfo.isScheduled,
                  //   scheduledStartTime: songInfo.scheduledStartTime
                  // });

                  // Notify backend and wait for completion
                  try {
                    await notifyBackendAboutAddedSong(songInfo);
                  } catch (error) {
                    console.error('Failed to notify backend, but continuing with UI flow:', error);
                  }

                  handleOk();
                  // Use values.searchedSongData which has the converted URI (not the original URL)
                  const songUriForShare = values.searchedSongData;
                  const obj = {
                    song_uri: songUriForShare,
                    selected_playlist_id: playlistId || values.playlistId,
                    id: songUriForShare?.split('spotify:track:')[1],
                    position: values.position,
                    started_time: toSafeIsoString(scheduledStart),
                    endTime: values.endTime || data.endTime,
                  };
                  localStorage.setItem('instantShareDetail', JSON.stringify(obj));

                  toast.success('Song added successfully');
                  showLoading(false);

                  // If this is from a campaign, redirect back to campaigns page
                  if (isFromCampaign) {
                    // Set a longer timeout to ensure the backend notification completes
                    setTimeout(() => {
                      window.location.href = '/campaigns?songAdded=true';
                    }, 1000);
                  } else {
                    // For non-campaign flows, reload the current page
                    window.location.reload();
                  }
                }
              })
              .catch(error => {
                toast.error(formatPlaylistAddError(error));
                if (lastValue) {
                  showLoading(false);
                }
              });
          } else {
            toast.error('Start Time is required');
          }
        } else {
          toast.error('Song URI is not correct');
          showLoading(false);
        }
      } else {
        axios
          .get(`https://api.spotify.com/v1/tracks/${values?.searchedSongData?.split(':')[2]}`, {
            headers: {
              Authorization: `Bearer ${loginState.token}`,
            },
          })
          .then(async res => {
            // Check if end date is required for campaigns
            if (isFromCampaign && !values.endTime) {
              toast.error('End date is required for campaigns');
              showLoading(false);
              return;
            }

            await addSongToPlaylist(
              Array.isArray(values.playlistId) ? values.playlistId : [values.playlistId],
              values.searchedSongData,
              playlistAuth.accessToken,
              playlistAuth.refreshToken,
              playlistAuth.spotifyUserId,
              values.position,
              values.startTime,
              values.endTime,
              release === 'Later' ? 'future' : 'past',
              note,
              getCampaignId(),
              values.artist_name,
              values.song_name,
              playlistAuth.profileObjectId
            )
              .then(async response => {
                if (lastValue) {
                  // Get playlist details for notification
                  const playlistDetails = totalPlaylist.find(
                    playlist => playlist.id === values.playlistId
                  );
                  const playlistName = playlistDetails?.name || 'Unknown Playlist';

                  // Create campaign song info
                  const isCurrentlyUnreleased = !!(
                    isFromCampaign &&
                    campaignData &&
                    !campaignData.track_released
                  );
                  const songInfo: CampaignSongInfo = {
                    campaignId: getCampaignId(),
                    songPosition: values.position,
                    playlistId: values.playlistId,
                    playlistName: playlistName,
                    songURI: values.searchedSongData,
                    songShareLink:
                      res.data.external_urls?.spotify ||
                      `https://open.spotify.com/track/${values.searchedSongData.split(':')[2]}`,
                    addedBy: '', // This will be set in notifyBackendAboutAddedSong
                    status: release === 'Later' ? 'future' : 'past',
                    startTime: values.startTime
                      ? toSafeIsoString(values.startTime)
                      : toSafeIsoString(setDate),
                    endTime: values.endTime || null,
                    notes: note,
                    isScheduled: isCurrentlyUnreleased,
                    scheduledStartTime: isCurrentlyUnreleased
                      ? toSafeIsoString(setDate ?? values.startTime)
                      : null,
                  };

                  // Notify backend and wait for completion
                  try {
                    await notifyBackendAboutAddedSong(songInfo);
                  } catch (error) {
                    console.error('Failed to notify backend, but continuing with UI flow:', error);
                  }

                  //console.log('sdasdasdsa')
                  handleOk();
                  // Use values.searchedSongData which has the converted URI (not the original URL)
                  const songUriForShare = values.searchedSongData;
                  const obj = {
                    song_uri: songUriForShare,
                    selected_playlist_id: playlistId || values.playlistId,
                    id: songUriForShare?.split('spotify:track:')[1],
                    position: values.position,
                    started_time: values.startTime ? values.startTime : setDate,
                    endTime: values.endTime || data.endTime,
                  };
                  localStorage.setItem('instantShareDetail', JSON.stringify(obj));

                  toast.success('Song added successfully');
                  showLoading(false);

                  // If this is from a campaign, redirect back to campaigns page
                  if (isFromCampaign) {
                    // Set a longer timeout to ensure the backend notification completes
                    setTimeout(() => {
                      window.location.href = '/campaigns?songAdded=true';
                    }, 1000);
                  } else {
                    // For non-campaign flows, reload the current page
                    window.location.reload();
                  }
                }
              })
              .catch(error => {
                toast.error(formatPlaylistAddError(error));
                showLoading(false);
              });
          })
          .catch(e => {
            toast.error('Song URI is not correct');
            showLoading(false);
          });
      }
    }
    //console.log('dsadsad')
  };

  /**
   * This function is filtering the playlists for dropdown based on owner
   * because we want the dropdown to show only those playlists scheduled by
   * songplace
   *
   * @returns {playlist[]} filtered playlists
   */
  useEffect(() => {
    if (preRelease) {
      setRelease('Later');
      handleSwitch(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preRelease]);

  // Set release to "Immediately" when isFromCampaign is true
  useEffect(() => {
    if (isFromCampaign) {
      if (isUnreleasedCampaign) {
        // For unreleased campaigns, force "Later" mode
        setRelease('Later');
        handleSwitch(true); // This sets setButtom="disabled" to show date picker
        let obj = { ...data };
        obj.startTime = ''; // Will be set by user
        obj.endTime = '';
        setData(obj);
      } else {
        // For released campaigns, keep existing behavior
        setRelease('Immediately');
        handleSwitch(false);
        let obj = { ...data };
        obj.startTime = '';
        obj.endTime = '';
        setData(obj);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFromCampaign, isUnreleasedCampaign]);

  // Add an effect specifically for handling campaign data when modal opens
  useEffect(() => {
    if (isModalVisible && isFromCampaign && campaignData) {
      // Pre-fill artist name and song name from campaign data
      setData(prevData => ({
        ...prevData,
        artist_name: campaignData.artist_name || '',
        song_name: campaignData.track_name || '',
      }));

      // Set song URI if available
      if (campaignData.spotify_uri) {
        setData(prevData => ({
          ...prevData,
          searchedSongData: campaignData.spotify_uri,
        }));
      }
    }
  }, [isModalVisible, isFromCampaign, campaignData]);

  return (
    <AddToPlayListModalStyle
      visible={isModalVisible}
      onOk={handleOk}
      footer={null}
      onCancel={handleCancel}
    >
      <div
        onClick={() => {
          setSelectPlayListOpen(false);
        }}
      >
        <div className="addToPlaylistHead">
          <h1 className="heading">{pre_Release ? 'Schedule your songs' : 'Add to playlist'}</h1>
          <p className="description">
            To schedule a song in your playlist, please select the playlist below.
          </p>
        </div>
        <div className="formAddToPlaylist">
          <div className="formPlaylist">
            <div className="playlistPosition">
              <label className="label">
                <p>Playlist *</p>
                <p className="limitText">{limitText}</p>
                <div
                  className="playlistInput"
                  onClick={e => {
                    e.stopPropagation();
                    setSelectPlayListOpen(true);
                  }}
                >
                  <div className="playlistInputText">
                    {selectedPlayList.map((a, i) => (
                      <span>{a}</span>
                    ))}
                    <input
                      className="playlistInputTextSearch"
                      value={searchPlaylistText}
                      placeholder="Search Playlist"
                      onChange={ev => {
                        setSearchPlaylistText(ev.target.value);
                      }}
                    />
                  </div>
                  <div
                    className={`optionsList ${selectPlayListOpen ? 'active' : ''}`}
                    onClick={e => {
                      e.stopPropagation();
                    }}
                  >
                    {isAccountPlaylistPickerLoading ? (
                      <div className="optionName">
                        <div className="optionText">
                          <p>
                            {usesAccountCampaignPlaylists
                              ? 'Loading playlists from your assigned profiles...'
                              : 'Loading playlists from your linked profiles...'}
                          </p>
                        </div>
                      </div>
                    ) : getPlaylistItems(totalPlaylist).filter(option =>
                        option?.value?.toLowerCase().includes(searchPlaylistText?.toLowerCase())
                      )?.length ? (
                      getPlaylistItems(totalPlaylist)
                        .filter(option =>
                          option?.value?.toLowerCase().includes(searchPlaylistText?.toLowerCase())
                        )
                        .map((option: { key: string; value: string; disabled?: boolean }) => {
                          return (
                            <div
                              className="optionName"
                              id="add-songs-mode-options"
                              onClick={value => {
                                value.stopPropagation();
                                if (data.playlistId.includes(option.key)) {
                                  setLimitText('');
                                  let obj = { ...data };
                                  obj.playlistId.splice(obj.playlistId.indexOf(option.key), 1);
                                  // @ts-ignore
                                  setData(obj);
                                  let playlist = [...selectedPlayList];
                                  playlist.splice(playlist.indexOf(option.value), 1);
                                  setSelectedPlayList([...playlist]);
                                } else {
                                  if (
                                    user?.free &&
                                    data?.playlistId.length >= 1 &&
                                    !isFromCampaign
                                  ) {
                                    setLimitText('Upgrade your plan to select multiple playlists');
                                  } else {
                                    setLimitText('');
                                    let obj = { ...data };
                                    // @ts-ignore
                                    obj.playlistId = [...obj.playlistId, option.key];
                                    let playlist = [...selectedPlayList];
                                    playlist = [...playlist, option.value];
                                    setSelectedPlayList([...playlist]);
                                    setData(obj);
                                  }
                                }
                              }}
                            >
                              <div className="optionText">
                                <p>{option.value}</p>
                                <div
                                  className={`optionMark ${
                                    data.playlistId.includes(option.key) ? 'active' : ''
                                  }`}
                                >
                                  <svg
                                    width="13"
                                    height="10"
                                    viewBox="0 0 13 10"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M1.5 5.5L5 9L12 1.5"
                                      stroke="white"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          );
                        })
                    ) : (
                      <div className="noResultText">
                        {usesAccountCampaignPlaylists
                          ? 'No submission playlists found for the profiles assigned to this campaign.'
                          : usesAccountWidePlaylists
                            ? 'No playlists found for your linked profiles.'
                            : 'No results found'}
                      </div>
                    )}
                  </div>
                </div>
              </label>
              <label className="label">
                <p>Position of the song in playlist *</p>

                {isFromCampaign || selectedPlayList.length > 1 ? (
                  <Input
                    type="number"
                    style={{ border: 'none', padding: '7.5px 10px' }}
                    min={1}
                    bordered={false}
                    value={data.position}
                    defaultValue={data.position}
                    placeholder="Position of the song in playlist"
                    onChange={value => {
                      let obj = { ...data };
                      obj.position = Number(value.target.value);
                      setData(obj);
                    }}
                  />
                ) : (
                  <>
                    <Popover
                      content={
                        <p>
                          {isFromCampaign
                            ? 'Single position entry for campaigns'
                            : 'Use space or enter to add multiple positions'}
                        </p>
                      }
                      placement="bottomLeft"
                    >
                      <div className="playlistInput">
                        <div className="playlistInputText">
                          {arrayPositions.map((a, i) => (
                            <span>{a}</span>
                          ))}
                          <Input
                            type="number"
                            min={1}
                            bordered={false}
                            value={positionText === 0 ? 'Please enter position' : positionText}
                            placeholder="Position of the song in playlist"
                            onChange={value => {
                              setPositionText(Number(value.target.value));
                            }}
                            onBlur={ev => {
                              if (ev.currentTarget.value) {
                                let arr = [...arrayPositions, ev.currentTarget.value];
                                setArrayPositions(arr);
                                setPositionText(0);
                              }
                            }}
                            onKeyDown={ev => {
                              if (ev.keyCode === 13 || ev.keyCode === 32) {
                                if (ev.currentTarget.value) {
                                  let arr = [...arrayPositions, ev.currentTarget.value];
                                  //console.log(arr)
                                  setArrayPositions(arr);
                                  setPositionText(0);
                                }
                              } else if (ev.keyCode === 8) {
                                if (!ev.currentTarget.value) {
                                  let arr = [...arrayPositions];
                                  arr.pop();
                                  setArrayPositions(arr);
                                  setPositionText(0);
                                }
                              }
                            }}
                          />
                        </div>
                      </div>
                    </Popover>
                  </>
                )}
              </label>
            </div>
            {hideUriInput ? null : (
              <>
                <div className="release" style={{ marginTop: '20px' }}>
                  <label
                    onClick={() => {
                      if (!isFromCampaign) {
                        setInputType('uri');
                      }
                    }}
                  >
                    <div className="radioCheck">{inputType === 'uri' ? <div></div> : null}</div>
                    URI
                  </label>
                  <label
                    onClick={() => {
                      if (!isFromCampaign) {
                        setInputType('url');
                      }
                    }}
                  >
                    <div className="radioCheck">{inputType === 'url' ? <div></div> : null}</div>
                    URL
                  </label>
                </div>
                <label className="label">
                  <p>Song {inputType === 'uri' ? 'URI' : 'URL'} *</p>
                  <Input
                    // type="text"
                    placeholder={inputType === 'uri' ? 'Song URI' : 'Song URL'}
                    value={data.searchedSongData}
                    disabled={isFromCampaign}
                    onChange={(value: any) => {
                      let obj = { ...data };
                      obj.searchedSongData = value.target.value;
                      setData(obj);
                    }}
                  />
                </label>

                {/* Phase 2: Song Preview */}
                {inputType === 'url' && (previewLoading || songPreview) && (
                  <div
                    style={{
                      marginTop: '15px',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #333',
                    }}
                  >
                    {previewLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Spinner />
                        <span style={{ color: '#999' }}>Loading song details...</span>
                      </div>
                    ) : songPreview ? (
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        {songPreview.album?.images?.[2]?.url && (
                          <img
                            src={songPreview.album.images[2].url}
                            alt="Album cover"
                            style={{ width: '64px', height: '64px', borderRadius: '4px' }}
                          />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ color: '#fff', fontWeight: '500', marginBottom: '4px' }}>
                            {songPreview.name}
                          </div>
                          <div style={{ color: '#999', fontSize: '14px' }}>
                            {songPreview.artists?.map(a => a.name).join(', ')}
                          </div>
                          {songPreview.album?.name && (
                            <div style={{ color: '#666', fontSize: '13px', marginTop: '2px' }}>
                              {songPreview.album.name}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </>
            )}
            {!hideArtistAndSongName || isFromCampaign ? (
              <div className="playlistPosition" style={{ marginTop: '20px' }}>
                <label className="label">
                  <p>Artist name</p>
                  <Input
                    // type="text"
                    placeholder="Artist name"
                    value={data.artist_name}
                    disabled={isFromCampaign}
                    onChange={(value: any) => {
                      let obj = { ...data };
                      obj.artist_name = value.target.value;
                      setData(obj);
                    }}
                  />
                </label>
                <label className="label">
                  <p>Song name</p>
                  <Input
                    placeholder="Song name"
                    value={data.song_name}
                    disabled={isFromCampaign}
                    onChange={(value: any) => {
                      let obj = { ...data };
                      obj.song_name = value.target.value;
                      setData(obj);
                    }}
                  />
                </label>
              </div>
            ) : null}
            {hideRadioButtons ? null : (
              <div className="release">
                {allowCampaignFlowForFree ? (
                  <>
                    <label
                      onClick={() => {
                        if (isUnreleasedCampaign) {
                          return;
                        }
                        setRelease('Immediately');
                        handleSwitch(false);
                        let obj = { ...data };
                        obj.startTime = '';
                        obj.endTime = '';
                        setData(obj);
                      }}
                    >
                      <div className="radioCheck">
                        {release === 'Immediately' ? <div></div> : null}
                      </div>
                      Immediately
                    </label>
                    {!(isFromCampaign && !isUnreleasedCampaign) && (
                      <label
                        onClick={() => {
                          if (!isUnreleasedCampaign) {
                            // Prevent changing if unreleased (it's already forced to Later)
                            setRelease('Later');
                            handleSwitch(true);
                            let obj = { ...data };
                            obj.startTime = '';
                            obj.endTime = '';
                            setData(obj);
                          }
                        }}
                      >
                        <div className="radioCheck">{release === 'Later' ? <div></div> : null}</div>
                        Later
                      </label>
                    )}
                  </>
                ) : (
                  <PlanToast
                    available={['pro', 'unlimited']}
                    titleHeading={'Schedule Start date'}
                    placement="right"
                  >
                    <label
                      onClick={() => {
                        if (isUnreleasedCampaign) {
                          return;
                        }
                        setRelease('Immediately');
                        handleSwitch(false);
                        let obj = { ...data };
                        obj.startTime = '';
                        obj.endTime = '';
                        setData(obj);
                      }}
                    >
                      <div className="radioCheck">
                        {release === 'Immediately' ? <div></div> : null}
                      </div>
                      Immediately
                    </label>
                    {!(isFromCampaign && !isUnreleasedCampaign) && (
                      <label
                        onClick={() => {
                          if (!isUnreleasedCampaign) {
                            // Prevent changing if unreleased (it's already forced to Later)
                            setRelease('Later');
                            handleSwitch(true);
                            let obj = { ...data };
                            obj.startTime = '';
                            obj.endTime = '';
                            setData(obj);
                          }
                        }}
                      >
                        <div className="radioCheck">{release === 'Later' ? <div></div> : null}</div>
                        Later
                      </label>
                    )}
                  </PlanToast>
                )}
              </div>
            )}
            {isUnreleasedCampaign && (
              <div
                style={{
                  padding: '12px',
                  backgroundColor: '#ff980020',
                  border: '1px solid #ff9800',
                  borderRadius: '4px',
                  marginTop: '16px',
                  marginBottom: '16px',
                }}
              >
                <p style={{ margin: 0, color: '#ff9800', fontSize: '14px' }}>
                  ⚠️ This song has not been released yet. It will be added to your playlist on the
                  selected release date.
                </p>
              </div>
            )}
            <div className="playlistPosition" style={{ marginTop: preRelease ? '20px' : 'auto' }}>
              {setButtom ? (
                <label className="label">
                  <p>Start date</p>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    {ADD_TO_PLAYLIST_DATE_QUICK_OFFSETS.map(({ label, unit, value }) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => {
                          const startDate = moment()
                            .add(value, unit as moment.unitOfTime.DurationConstructor)
                            .startOf('day');
                          onHandle(startDate, startDate.format('DD/MM/YYYY hh:mm A'));
                          let obj = { ...data };
                          obj.startTime = startDate.toISOString();
                          setData(obj);
                        }}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '8px',
                          border: '1px solid #242A34',
                          background: 'transparent',
                          color: '#999',
                          fontSize: '11px',
                          cursor: 'pointer',
                          transition: 'background 0.2s, color 0.2s',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLButtonElement).style.background = '#242A34';
                          (e.currentTarget as HTMLButtonElement).style.color = '#fff';
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                          (e.currentTarget as HTMLButtonElement).style.color = '#999';
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <DatePicker
                    showNow={false}
                    type="datetime-local"
                    value={data.startTime ? moment(data.startTime) : moment()}
                    format="DD/MM/YYYY hh:mm A"
                    onChange={(value, valueDate) => {
                      if (isPastStartTime(value)) {
                        toast.error(PAST_START_TIME_MESSAGE);
                        return;
                      }
                      onHandle(value, valueDate);
                      let obj = { ...data };
                      obj.startTime = value ? value.toISOString() : undefined;
                      setData(obj);
                    }}
                    // Disable past dates
                    disabledDate={current => {
                      // Existing validation for past dates
                      const isPast = current && current < moment().startOf('day');

                      // NEW: For unreleased campaigns, enforce minimum date
                      if (isUnreleasedCampaign && campaignData.start_date) {
                        const releaseDate = moment(campaignData.start_date).startOf('day');
                        const isBeforeRelease = current && current < releaseDate;
                        return isPast || isBeforeRelease;
                      }

                      return isPast;
                    }}
                    // Disable past times if the selected date is today
                    disabledTime={current => {
                      const now = moment();
                      if (current && current.isSame(now, 'day')) {
                        return {
                          disabledHours: () => Array.from({ length: now.hours() }, (_, i) => i),
                          disabledMinutes: () => {
                            // Disable all past minutes in the current hour
                            if (current.hours() === now.hours()) {
                              return Array.from({ length: now.minutes() + 1 }, (_, i) => i);
                            }
                            return [];
                          },
                        };
                      }
                      return {};
                    }}
                  />
                </label>
              ) : null}
              <label className="label">
                <p>End date{isFromCampaign ? ' *' : ''}</p>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  {ADD_TO_PLAYLIST_DATE_QUICK_OFFSETS.map(({ label, unit, value }) => (
                    <button
                      key={label}
                      type="button"
                      disabled={!canUseCampaignSchedulingControls}
                      onClick={() => {
                        const base = data.startTime ? moment(data.startTime) : moment();
                        const endDate = base
                          .clone()
                          .add(value, unit as moment.unitOfTime.DurationConstructor)
                          .endOf('day');
                        let obj = { ...data };
                        obj.endTime = endDate.toISOString();
                        setData(obj);
                      }}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '8px',
                        border: '1px solid #242A34',
                        background: 'transparent',
                        color: '#999',
                        fontSize: '11px',
                        cursor: !canUseCampaignSchedulingControls ? 'not-allowed' : 'pointer',
                        transition: 'background 0.2s, color 0.2s',
                        opacity: !canUseCampaignSchedulingControls ? 0.5 : 1,
                        whiteSpace: 'nowrap',
                      }}
                      onMouseEnter={e => {
                        if (canUseCampaignSchedulingControls) {
                          (e.currentTarget as HTMLButtonElement).style.background = '#242A34';
                          (e.currentTarget as HTMLButtonElement).style.color = '#fff';
                        }
                      }}
                      onMouseLeave={e => {
                        if (canUseCampaignSchedulingControls) {
                          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                          (e.currentTarget as HTMLButtonElement).style.color = '#999';
                        }
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <DatePicker
                  showNow={false}
                  type="datetime-local"
                  format={'DD/MM/YYYY hh:mm A'}
                  value={data.endTime ? moment(data.endTime) : null}
                  disabled={!canUseCampaignSchedulingControls}
                  onChange={value => {
                    let obj = { ...data };
                    obj.endTime = value ? value.toISOString() : undefined;
                    setData(obj);
                  }}
                  defaultValue={
                    data.endTime === 'null' || data.endTime === '' ? '' : moment(`${data.endTime}`)
                  }
                  // Disable past dates
                  disabledDate={current => {
                    // Disable dates before today
                    const isPastDate = current && current < moment().startOf('day');
                    // Disable dates before the selected start date (if a start date is selected)
                    const isBeforeStartDate =
                      data.startTime && current && current < moment(data.startTime).startOf('day');
                    return isPastDate || isBeforeStartDate;
                  }}
                  disabledTime={current => {
                    const now = moment();
                    const startDateTime = data.startTime ? moment(data.startTime) : null;

                    // If current date is today
                    if (current && current.isSame(now, 'day')) {
                      return {
                        disabledHours: () => Array.from({ length: now.hours() }, (_, i) => i),
                        disabledMinutes: () => {
                          // Disable all past minutes in the current hour
                          if (current.hours() === now.hours()) {
                            return Array.from({ length: now.minutes() + 1 }, (_, i) => i);
                          }
                          return [];
                        },
                      };
                    }

                    // If current date is same as start date, disable times before start time
                    if (startDateTime && current && current.isSame(startDateTime, 'day')) {
                      return {
                        disabledHours: () =>
                          Array.from({ length: startDateTime.hours() }, (_, i) => i),
                        disabledMinutes: () => {
                          // Disable all minutes before the start time minutes in the same hour
                          if (current.hours() === startDateTime.hours()) {
                            return Array.from({ length: startDateTime.minutes() + 1 }, (_, i) => i);
                          }
                          return [];
                        },
                      };
                    }

                    return {};
                  }}
                />
              </label>
            </div>
          </div>

          <label className="label addNoteTextLabel">
            <PlanToast available={['pro', 'unlimited']} titleHeading={'Add note'} placement="right">
              <p style={{ marginTop: preRelease ? '20px' : 'auto' }}>Add note</p>
              <Input.TextArea
                className="addNoteText"
                placeholder="Write your notes here"
                rows={4}
                value={note}
                disabled={user?.free}
                onChange={ev => {
                  setNote(ev.target.value);
                }}
              />
            </PlanToast>
          </label>
        </div>
        <div className="buttonDiv">
          <button
            className="addSong"
            disabled={setLoading || isAccountPlaylistPickerLoading || !data.playlistId?.length ? true : false}
            onClick={async () => {
              showLoading(true);
              try {
                await addToPlaylistLoop({ ...data });
              } catch (error) {
                console.error('Error in addToPlaylistLoop:', error);
                toast.error(formatPlaylistAddError(error));
                showLoading(false);
              }
            }}
          >
            {setLoading ? <Spinner /> : 'Add'}
          </button>
        </div>
      </div>
    </AddToPlayListModalStyle>
  );
};

export default AddToPlayListModal;
